import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Col, DatePicker, List, Row, Tag, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { RightOutlined } from '@ant-design/icons';

import type { ReporteListItem } from '../../api';
import { EmptyState, Loader } from '../../components';
import { cleanEmpresa } from '../../utils/format';
import { useEmpresa, EMPRESA_TODAS } from '../../empresa';
import { useFiltroFecha } from '../../filtroFecha';

const { Title, Paragraph, Text } = Typography;

/**
 * Selector de RFC para entrar a conciliación manual.
 * Lista solo los reportes con `sin_cfdi > 0` (los unicos que tienen
 * Movimientos para conciliar manualmente), acotados a la empresa activa en
 * el selector global (Header) cuando hay una seleccionada.
 */
export function ManualSelect() {
  const navigate = useNavigate();
  const { rfc, reportes, nombresClientes, loading } = useEmpresa();
  // Tema 6 (segunda ronda ASI) — filtro mensual reemplazado por rango de
  // fecha inicial/final, compartido entre pantallas (mismo criterio que
  // ReporteDetalle/CargaArchivos/CfdiCatalogo). Un reporte matchea si su
  // período se superpone con el rango elegido (no exige que el período
  // completo del reporte esté contenido dentro del rango).
  const { rango, setRango } = useFiltroFecha();

  // Atajo de mes completo sobre el mismo `rango` — no es un filtro aparte.
  const mesSeleccionado: Dayjs | null = useMemo(() => {
    const [desde, hasta] = rango ?? [null, null];
    if (!desde || !hasta) return null;
    const esMesCompleto = desde.isSame(desde.clone().startOf('month'), 'day')
      && hasta.isSame(desde.clone().endOf('month'), 'day');
    return esMesCompleto ? desde : null;
  }, [rango]);

  function onMesChange(v: Dayjs | null) {
    setRango(v ? [v.startOf('month'), v.endOf('month')] : null);
  }

  function nombreDe(r: ReporteListItem): string {
    return nombresClientes[r.rfc.toUpperCase()] || (r.nombre_empresa ? cleanEmpresa(r.nombre_empresa) : '');
  }

  const conPendientes = useMemo(() => {
    return reportes.filter(r => (r?.resumen?.sin_cfdi ?? 0) > 0);
  }, [reportes]);

  const filtrados = useMemo(() => {
    const [desde, hasta] = rango ?? [null, null];
    return conPendientes.filter(r => {
      if (rfc !== EMPRESA_TODAS && r.rfc !== rfc) return false;
      if (desde && r.periodo?.fin && r.periodo.fin < desde.format('YYYY-MM-DD')) return false;
      if (hasta && r.periodo?.inicio && r.periodo.inicio > hasta.format('YYYY-MM-DD')) return false;
      return true;
    });
  }, [conPendientes, rfc, rango]);

  // Salvaguarda visual: aunque el backend ya deba garantizar una sola
  // conciliación por RFC + período exacto, agrupamos y mostramos solo la más
  // reciente. Se compara el período completo (inicio Y fin), no solo el mes
  // de inicio — dos conciliaciones distintas del mismo RFC pueden arrancar el
  // mismo mes sin ser duplicados reales (ej. 5 ene→5 feb y 20 ene→10 mar).
  const deduplicados = useMemo(() => {
    const porClave = new Map<string, ReporteListItem>();
    for (const r of filtrados) {
      const clave = `${r.rfc}|${r.periodo?.inicio ?? ''}|${r.periodo?.fin ?? ''}`;
      const actual = porClave.get(clave);
      if (!actual || (r.fecha_ejecucion ?? '') > (actual.fecha_ejecucion ?? '')) {
        porClave.set(clave, r);
      }
    }
    return Array.from(porClave.values());
  }, [filtrados]);

  if (loading) return <Loader tip="Cargando Usuarios..." />;

  return (
    <div style={{ maxWidth: 720 }}>
      <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>Conciliación Manual</Title>
      <Paragraph type="secondary" style={{ marginBottom: 20 }}>
        Selecciona un Usuario para Revisar sus Movimientos sin CFDI.
      </Paragraph>

      {conPendientes.length === 0 ? (
        <Card>
          <EmptyState description="No Hay Movimientos Pendientes de Conciliación Manual." />
        </Card>
      ) : (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={10}>
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                value={rango ?? [null, null]}
                onChange={v => setRango(v ? [v[0], v[1]] : null)}
                format="YYYY-MM-DD"
                placeholder={['Desde', 'Hasta']}
              />
            </Col>
            <Col xs={24} sm={8}>
              <DatePicker
                picker="month"
                style={{ width: '100%' }}
                value={mesSeleccionado}
                onChange={onMesChange}
                format="MMMM YYYY"
                placeholder="Filtrar por mes"
                allowClear
              />
            </Col>
          </Row>

          {deduplicados.length === 0 ? (
            <Card>
              <EmptyState description="Ningún Reporte Coincide con los Filtros." />
            </Card>
          ) : (
            <Card styles={{ body: { padding: 0 } }}>
              <List
                dataSource={deduplicados}
                renderItem={(r) => (
                  <List.Item
                    key={r.id || `${r.rfc}_${r.fecha_ejecucion ?? ''}`}
                    onClick={() => navigate(`/reportes/${r.rfc}/match${r.id ? `?id=${r.id}` : ''}`)}
                    style={{ cursor: 'pointer', padding: '14px 20px' }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div>
                        <Tag style={{ fontFamily: 'monospace' }}>{r.rfc}</Tag>
                        {nombreDe(r) && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {nombreDe(r)}
                          </Text>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#dc3545', marginTop: 4 }}>
                        {r.resumen.sin_cfdi} Movimiento{r.resumen.sin_cfdi !== 1 ? 's' : ''} sin CFDI
                      </div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {r.periodo?.inicio && r.periodo?.fin && (
                          <span>{r.periodo.inicio} → {r.periodo.fin}</span>
                        )}
                        {r.fecha_ejecucion && (
                          <span style={{ marginLeft: 8 }}>· Conciliado {r.fecha_ejecucion.slice(0, 10)}</span>
                        )}
                      </Text>
                    </div>
                    <RightOutlined style={{ color: '#adb5bd' }} />
                  </List.Item>
                )}
              />
            </Card>
          )}
        </>
      )}
    </div>
  );
}
